import React, { useCallback, useEffect, useState } from "react";
import { useApi } from "../hooks/useApi";

// An audit engagement's findings (Audit Management 5A step 11). A finding is raised from a test and
// gets its risk in the risk register at the same time; the risk carries only "Audit finding AUD-n-m",
// the severity and the requirement — the finding's text stays here, for the audit team. Lifecycle:
// Open → Action planned (management response, owner, due date: the risk is assigned to the owner) →
// Remediated (the owner closed the risk) → Validated (a follow-up test signed off as effective) →
// Closed (a reviewer or the lead who is not the action owner). Follow-up tests are listed under their
// finding and stay workable after the audit closes. The server says what this person may do.

export const FINDING_STATE_LABEL = {
    Open: "Open",
    ActionPlanned: "Action planned",
    Remediated: "Remediated",
    Validated: "Validated",
    Closed: "Closed",
};

const FINDING_STATE_STYLE = {
    Open: "bg-red-50 text-red-700 border-red-200",
    ActionPlanned: "bg-amber-50 text-amber-700 border-amber-200",
    Remediated: "bg-blue-50 text-blue-700 border-blue-200",
    Validated: "bg-green-50 text-green-700 border-green-200",
    Closed: "bg-gray-50 text-gray-500 border-gray-200",
};

export const SEVERITIES = ["Critical", "High", "Medium", "Low"];

const SEVERITY_STYLE = {
    Critical: "text-red-700",
    High: "text-orange-700",
    Medium: "text-amber-700",
    Low: "text-gray-600",
};

const messageOf = (err, fallback) => err?.response?.data?.message || fallback;

// One key per form opened: a retried send of the same form returns the finding already raised.
const newKey = () => (typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

const TEXT_FIELDS = [
    ["condition", "Condition (what was found)", true],
    ["criteria", "Criteria (what it was tested against)", true],
    ["cause", "Cause", false],
    ["effect", "Effect", false],
    ["recommendation", "Recommendation", false],
];

const emptyFinding = (testId = "") => ({
    testId, title: "", severity: "", condition: "", criteria: "", cause: "", effect: "", recommendation: "",
});

// The edit form's values: the finding's fields, blanks for the ones never filled in.
const formOf = (finding) => Object.fromEntries(
    ["title", "severity", ...TEXT_FIELDS.map(([key]) => key)].map(key => [key, finding[key] || ""]));

const FindingForm =({ form, set, tests, onSubmit, onCancel, submitLabel }) => (
    <div className="border border-gray-200 rounded p-2 bg-white grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="audit-finding-form">
        {tests && (
            <label className="flex flex-col gap-1">Test
                <select value={form.testId} onChange={e => set({ testId: e.target.value })} className="border border-gray-200 rounded px-2 py-1 bg-white">
                    <option value="">Choose the test</option>
                    {tests.map(t => <option key={t.id} value={t.id}>{t.requirementDisplayId} — {t.requirementTitle}</option>)}
                </select>
            </label>
        )}
        <label className="flex flex-col gap-1">Title
            <input value={form.title} onChange={e => set({ title: e.target.value })} maxLength={500} className="border border-gray-200 rounded px-2 py-1" />
        </label>
        <label className="flex flex-col gap-1">Severity
            <select value={form.severity} onChange={e => set({ severity: e.target.value })} className="border border-gray-200 rounded px-2 py-1 bg-white">
                <option value="">Choose the severity</option>
                {SEVERITIES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
        </label>
        {TEXT_FIELDS.map(([key, label]) => (
            <label key={key} className="flex flex-col gap-1 md:col-span-2">{label}
                <textarea value={form[key]} onChange={e => set({ [key]: e.target.value })} rows={2} className="border border-gray-200 rounded px-2 py-1" />
            </label>
        ))}
        <div className="flex gap-2 md:col-span-2">
            <button type="button" onClick={onSubmit}
                disabled={(tests && !form.testId) || !form.title.trim() || !form.severity || !form.condition.trim() || !form.criteria.trim()}
                className="px-2 py-0.5 rounded bg-indigo-600 text-white disabled:opacity-50">{submitLabel}</button>
            <button type="button" onClick={onCancel} className="text-gray-500 hover:underline">Cancel</button>
        </div>
    </div>
);

const ActionPlanForm = ({ finding, people, onSubmit, onCancel }) => {
    const [form, setForm] = useState({
        managementResponse: finding.managementResponse || "",
        actionOwnerUserId: finding.actionOwnerUserId ? String(finding.actionOwnerUserId) : "",
        actionDueDate: finding.actionDueDate ? finding.actionDueDate.slice(0, 10) : "",
    });
    const set = (patch) => setForm(f => ({ ...f, ...patch }));
    return (
        <div className="border border-gray-200 rounded p-2 bg-white grid grid-cols-1 md:grid-cols-2 gap-2" data-testid={`audit-action-plan-${finding.id}`}>
            <label className="flex flex-col gap-1 md:col-span-2">Management response
                <textarea value={form.managementResponse} onChange={e => set({ managementResponse: e.target.value })} rows={2} className="border border-gray-200 rounded px-2 py-1" />
            </label>
            <label className="flex flex-col gap-1">Action owner
                <select value={form.actionOwnerUserId} onChange={e => set({ actionOwnerUserId: e.target.value })} className="border border-gray-200 rounded px-2 py-1 bg-white">
                    <option value="">Choose the owner</option>
                    {(people || []).map(p => <option key={p.id} value={p.id}>{p.fullName}</option>)}
                </select>
            </label>
            <label className="flex flex-col gap-1">Due by
                <input type="date" value={form.actionDueDate} onChange={e => set({ actionDueDate: e.target.value })} className="border border-gray-200 rounded px-2 py-1" />
            </label>
            <div className="flex gap-2 md:col-span-2">
                <button type="button" disabled={!form.managementResponse.trim() || !form.actionOwnerUserId || !form.actionDueDate}
                    onClick={() => onSubmit({ ...form, actionOwnerUserId: Number(form.actionOwnerUserId) })}
                    className="px-2 py-0.5 rounded bg-indigo-600 text-white disabled:opacity-50">Save action plan</button>
                <button type="button" onClick={onCancel} className="text-gray-500 hover:underline">Cancel</button>
            </div>
        </div>
    );
};

const formatDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : null);

const FindingRow = ({ finding, open, onToggle, act, people, loadPeople, followUps, renderFollowUp }) => {
    const [editing, setEditing] = useState(null); // null | "finding" | "plan"
    const [form, setForm] = useState(null);
    const base = `findings/${finding.id}`;
    const run = (method, path, body) => act(method, path, body).then(() => setEditing(null), () => {});
    const openFollowUp = followUps.some(t => !["SignedOff", "NotTested", "OutOfScope"].includes(t.state));
    return (
        <li data-testid={`audit-finding-${finding.reference}`} className={open ? "bg-gray-50" : ""}>
            <button type="button" onClick={onToggle} aria-expanded={open}
                className="w-full text-left px-3 py-2 flex flex-wrap items-start gap-x-3 gap-y-1 hover:bg-gray-50 text-xs">
                <span className="font-semibold text-gray-800 w-24 shrink-0">{finding.reference}</span>
                <span className="text-gray-800 flex-1 min-w-[12rem]">{finding.title}</span>
                <span className="text-gray-500 w-20 shrink-0">{finding.requirementDisplayId}</span>
                <span className={`w-16 shrink-0 font-semibold ${SEVERITY_STYLE[finding.severity] || ""}`}>{finding.severity}</span>
                <span className={`px-2 py-0.5 rounded-full border font-semibold ${FINDING_STATE_STYLE[finding.state] || FINDING_STATE_STYLE.Open}`}>
                    {FINDING_STATE_LABEL[finding.state] || finding.state}
                </span>
            </button>
            {open && (
                <div className="px-3 pb-3 space-y-2 text-xs" data-testid={`audit-finding-detail-${finding.reference}`}>
                    {editing === "finding" ? (
                        <FindingForm form={form} set={(p) => setForm(f => ({ ...f, ...p }))} submitLabel="Save finding"
                            onSubmit={() => run("PUT", base, form)} onCancel={() => setEditing(null)} />
                    ) : (
                        <dl className="grid grid-cols-1 md:grid-cols-[10rem_1fr] gap-x-3 gap-y-1 text-gray-700">
                            {TEXT_FIELDS.map(([key, label]) => finding[key] && (
                                <React.Fragment key={key}><dt className="text-gray-500">{label.split(" (")[0]}</dt><dd className="whitespace-pre-wrap">{finding[key]}</dd></React.Fragment>
                            ))}
                            {finding.managementResponse && (<><dt className="text-gray-500">Management response</dt><dd className="whitespace-pre-wrap">{finding.managementResponse}</dd></>)}
                            {finding.actionOwnerName && (<><dt className="text-gray-500">Action</dt><dd>{finding.actionOwnerName} · due {formatDate(finding.actionDueDate)}</dd></>)}
                            <dt className="text-gray-500">Risk register</dt>
                            <dd>{finding.riskStatus == null ? <span className="text-red-600">The finding's risk is missing.</span>
                                : `Audit finding ${finding.reference} — ${finding.riskStatus === "closed" ? "closed" : "open"}`}</dd>
                            {finding.closedAt && (<><dt className="text-gray-500">Closed</dt><dd>{formatDate(finding.closedAt)}{finding.closedByName ? ` by ${finding.closedByName}` : ""}</dd></>)}
                        </dl>
                    )}

                    {editing === "plan" && (
                        <ActionPlanForm finding={finding} people={people} onCancel={() => setEditing(null)}
                            onSubmit={(body) => run("PUT", `${base}/action-plan`, body)} />
                    )}

                    {!editing && (
                        <div className="flex flex-wrap gap-2">
                            {finding.canEdit && (
                                <button type="button" onClick={() => { setForm(formOf(finding)); setEditing("finding"); }}
                                    className="px-2 py-0.5 border border-gray-200 rounded hover:bg-gray-100">Edit finding</button>
                            )}
                            {finding.canEdit && (
                                <button type="button" onClick={() => { loadPeople(); setEditing("plan"); }}
                                    className="px-2 py-0.5 border border-indigo-200 rounded text-indigo-700 hover:bg-indigo-50">
                                    {finding.actionOwnerUserId ? "Change action plan" : "Record action plan"}
                                </button>
                            )}
                            {finding.canEdit && finding.actionOwnerUserId && !openFollowUp && (
                                <button type="button" onClick={() => run("POST", `${base}/follow-up-tests`)}
                                    className="px-2 py-0.5 border border-indigo-200 rounded text-indigo-700 hover:bg-indigo-50">Add follow-up test</button>
                            )}
                            {finding.canClose && (
                                <button type="button" onClick={() => run("POST", `${base}/close`)}
                                    className="px-2 py-0.5 rounded bg-green-600 text-white">Close finding</button>
                            )}
                            {finding.riskStatus == null && (
                                <button type="button" onClick={() => run("POST", `${base}/repair-risk`)}
                                    className="px-2 py-0.5 border border-red-200 rounded text-red-700 hover:bg-red-50">Recreate the risk</button>
                            )}
                        </div>
                    )}

                    {followUps.length > 0 && (
                        <div className="space-y-1">
                            <div className="font-semibold text-gray-600">Follow-up tests</div>
                            <ul className="divide-y divide-gray-100 border border-gray-200 rounded bg-white">
                                {followUps.map(renderFollowUp)}
                            </ul>
                        </div>
                    )}
                </div>
            )}
        </li>
    );
};

const AuditFindings = ({ engagement, tests, onChanged, renderFollowUp }) => {
    const apiCall = useApi();
    const base = `/security-insights/audits/${engagement.id}`;
    const [findings, setFindings] = useState(null);
    const [openId, setOpenId] = useState(null);
    const [form, setForm] = useState(null); // the Raise form; null = closed
    const [key, setKey] = useState(null);
    const [people, setPeople] = useState(null);
    const [error, setError] = useState(null);
    const active = engagement.status === "Fieldwork" || engagement.status === "Review";

    const load = useCallback(() => apiCall("GET", `${base}/findings`)
        .then(list => setFindings(Array.isArray(list) ? list : []))
        .catch(() => setFindings([])), [apiCall, base]);
    // Reloaded with the tests too: signing off a follow-up (in its test row) validates its finding.
    useEffect(() => { load(); }, [load, tests]);

    const loadPeople = () => {
        if (people == null)
            apiCall("GET", `${base}/people`)
                .then(list => setPeople(Array.isArray(list) ? list : []))
                .catch(() => setPeople([]));
    };

    // Every action: call, then reload the tests (a follow-up or close changes them), which reloads the findings.
    const act = async (method, path, body) => {
        setError(null);
        try {
            await apiCall(method, `${base}/${path}`, body);
            await onChanged();
        } catch (err) {
            setError(messageOf(err, "That did not go through. Try again."));
            throw err;
        }
    };

    const raise = () => act("POST", "findings", { ...form, testId: Number(form.testId), idempotencyKey: key })
        .then(() => setForm(null), () => {});

    const originals = tests.filter(t => t.kind !== "FollowUp" && t.state !== "OutOfScope");
    if (findings == null) return null;
    return (
        <div className="space-y-1" data-testid={`audit-findings-${engagement.id}`}>
            <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold text-gray-600 mr-auto">Findings ({findings.length})</span>
                {engagement.canWorkOnFindings && active && !form && originals.length > 0 && (
                    <button type="button" onClick={() => { setForm(emptyFinding()); setKey(newKey()); }}
                        className="px-2 py-0.5 border border-red-200 rounded text-red-700 hover:bg-red-50">Raise finding</button>
                )}
            </div>
            {form && (
                <FindingForm form={form} set={(p) => setForm(f => ({ ...f, ...p }))} tests={originals} submitLabel="Raise finding"
                    onSubmit={raise} onCancel={() => { setForm(null); setError(null); }} />
            )}
            {error && <div className="text-red-600" role="alert">{error}</div>}
            {findings.length === 0 && !form
                ? <div className="text-gray-400">No findings raised.</div>
                : (
                    <ul className="divide-y divide-gray-100 border border-gray-200 rounded bg-white">
                        {findings.map(f => (
                            <FindingRow key={f.id} finding={f} open={openId === f.id} onToggle={() => setOpenId(openId === f.id ? null : f.id)}
                                act={act} people={people} loadPeople={loadPeople}
                                followUps={tests.filter(t => t.kind === "FollowUp" && t.findingId === f.id)}
                                renderFollowUp={renderFollowUp} />
                        ))}
                    </ul>
                )}
        </div>
    );
};

export default AuditFindings;
